import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { icons } from "assets/AssetHelper";
import { useThemePalette } from "common/hooks/theme_palette";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPresignedUrlRequest } from "services/modules/uploadFiles";
import setAuthToken from "services/modules/utils/handel_api";
import CloseIcon from "@mui/icons-material/Close";
import CFormErrors from "./CFormErrors";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

function CUpload({
  placeholder,
  btnStyle,
  inputStyle,
  btnText,
  type,
  id,
  label,
  name,
  formik,
  changeAction,
  text,
  enableVideo = false,
}) {
  const {
    load: { loading },
  } = useSelector((state) => state?.files);
  const [fileUrl, setFileUrl] = useState(null);
  const [openLoader, setOpenLoader] = useState(false);
  const { orange, textGray, oliveGreen } = useThemePalette();
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const maxUploadSize = useSelector(
    (state) => state?.systemConfig?.systemConfigs
  ).filter((ele) => ele?.key == "max_upload_size")[0];

  const dispatch = useDispatch();
  const errorsMsg = formik?.errors[name];

  useEffect(() => {
    formik?.values[name] && setFileUrl(formik?.values[name]);
  }, [formik?.values[name]]);

  const handleClose = () => {
    setFileUrl(null);
    changeAction("");
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      uploadFile(files[0], type);
    }
  };

  const isVideoFile = (url) => {
    if (!url) return false;
    return (
      url.includes(".mp4") ||
      url.includes(".mov") ||
      url.includes(".avi") ||
      url.includes(".wmv") ||
      url.includes(".flv") ||
      url.includes(".webm") ||
      url.includes(".mkv") ||
      (typeof url === "object" && url?.type?.includes("video"))
    );
  };

  const uploadFile = async (file, type) => {
    const imageExe = ["png", "jpg", "jpeg"];
    const videoExe = ["mp4", "mov", "avi", "wmv", "flv", "webm", "mkv"];
    const allowedExe = enableVideo ? [...imageExe, ...videoExe] : imageExe;
    const extension = file?.type.slice(file?.type.indexOf("/") + 1);
    const isVideo = enableVideo && videoExe.includes(extension);
    const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024; // 100MB for videos, 10MB for images

    try {
      if (file.size > maxSize) {
        const sizeLimit = isVideo ? "100MB" : "10MB";
        setError(`File size must be less than ${sizeLimit}`);
        return;
      }

      if (!allowedExe?.includes(extension)) {
        const errorMessage = enableVideo
          ? "File must be png, jpg, jpeg, mp4, mov, avi, wmv, flv, webm, or mkv"
          : "File must be png, jpg, or jpeg";
        setError(errorMessage);
        return;
      }

      setError(null);
      setOpenLoader(true);

      const requestData = {
        body: {
          files: [
            {
              extension: extension,
            },
          ],
        },
        file: file,
        updateData: (data) => {
          changeAction(data);
          setFileUrl(data);
        },
        action: (data) => {
          setOpenLoader(false);
          const token = localStorage.getItem("token");
          setAuthToken(token);
        },
      };
      dispatch(getPresignedUrlRequest({ ...requestData }));
    } catch (error) {
      console.error("Error in uploadFile:", error);
      setOpenLoader(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      uploadFile(file, type);
    }
  };

  useEffect(() => {
    setError(formik?.errors[name]);
  }, [formik?.errors]);

  const currentUrl = fileUrl || formik?.values?.[name];
  const isVideo = currentUrl && isVideoFile(currentUrl);

  return (
    <Box>
      <Stack sx={{ mb: "15px" }}>
        <Typography
          sx={{ fontFamily: "Cairo", fontSize: "32px", fontWeight: "400" }}
        >
          {label}
        </Typography>
      </Stack>
      {currentUrl ? (
        <Box
          sx={{
            border: "1px dashed rgba(0, 0, 0, 0.25)",
            borderRadius: "10px",
            position: "relative",
          }}
          width={type == "icon" ? "95px" : "375px"}
          height={type == "icon" ? "95px" : isVideo ? "auto" : "300px"}
          minHeight={isVideo ? "200px" : undefined}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <Stack
            justifyContent="center"
            alignItems="center"
            width={"100%"}
            height={"100%"}
            sx={{
              position: "relative",
              overflow: "hidden",
              "&:hover .play-button": {
                opacity: 1,
                transform: "translate(-50%, -50%) scale(1.1)",
              },
            }}
          >
            {isVideo ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#f5f5f5",
                  position: "relative",
                  cursor: "pointer",
                  minHeight: "200px",
                }}
              >
                {/* <VideoFileIcon
                  sx={{
                    fontSize: "48px",
                    color: "#666",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                /> */}
                <PlayArrowIcon
                  className="play-button"
                  sx={{
                    fontSize: "48px",
                    color: orange,
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    opacity: 0.8,
                    transition: "all 0.3s ease",
                    ml: "8px",
                    "&:hover": {
                      opacity: 1,
                      transform: "translate(-50%, -50%) scale(1.2)",
                    },
                  }}
                />
              </Box>
            ) : (
              <Box
                component="img"
                src={currentUrl}
                alt="Uploaded content"
                width={"100%"}
                height={"100%"}
                sx={{
                  borderRadius: "10px",
                  objectFit: "cover",
                }}
              />
            )}
          </Stack>
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "48px",
              height: "48px",
              bgcolor: "white",
              borderRadius: "50%",
              filter: "drop-shadow(0px 0px 4px rgba(185, 185, 185, 0.25))",
              transform: "translate(50%, -30%)",
              cursor: "pointer",
              "&:hover": {
                bgcolor: "#f5f5f5",
              },
            }}
            onClick={handleClose}
          >
            <CloseIcon />
          </Stack>
        </Box>
      ) : (
        <>
          <Grid
            item
            xs={12}
            sx={{
              border: "1px dashed rgba(0, 0, 0, 0.25)",
              borderRadius: "10px",
              "&:hover": {
                borderColor: orange,
              },
              transition: "border-color 0.3s ease",
            }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <label htmlFor={`upload-${id}`} style={{ cursor: "pointer" }}>
              <input
                id={`upload-${id}`}
                name={`upload-${id}`}
                type="file"
                style={{ display: "none" }}
                onChange={handleFileInputChange}
                accept={enableVideo ? "image/*,video/*" : "image/*"}
              />
              <Stack alignItems="center" rowGap={"24px"} py="3.5rem">
                {loading && openLoader ? (
                  <CircularProgress sx={{ color: oliveGreen }} />
                ) : (
                  <>
                    <img src={icons?.uploadIcon} alt="upload icon" />
                    <Typography fontSize="13px">
                      {enableVideo
                        ? "Select an image or video file or drag and drop here"
                        : "Select a file or drag and drop here"}
                    </Typography>
                    {text && (
                      <Typography fontSize="12px" color={textGray}>
                        {text} {maxUploadSize?.value} KB
                        {enableVideo && " (Max 100MB for videos)"}
                      </Typography>
                    )}
                    <Button
                      component="span"
                      sx={{
                        color: orange,
                        border: `1px solid ${orange}`,
                        fontSize: "15px",
                        px: "16px",
                        "&:hover": {
                          bgcolor: "rgba(255, 152, 0, 0.04)",
                          borderColor: orange,
                        },
                      }}
                    >
                      {enableVideo ? "Select Media" : "Select File"}
                    </Button>
                  </>
                )}
              </Stack>
            </label>
          </Grid>
          <CFormErrors errorsMsg={error || errorsMsg} />
        </>
      )}
    </Box>
  );
}

export default CUpload;
